import { useMutation, useQuery } from "@tanstack/react-query"
import { Collection, Table } from "dexie"
import { collection, doc, runTransaction } from "firebase/firestore"
import {
   dexieQueryMaxAttempts,
   dexieQueryRetryDelay,
   firestoreTransactionMaxAttempts,
} from "../config/env"
import { db } from "../firebase/firebase"
import { dexie } from "../firestore/localDatabase"
import { createDocumentTransactionFn, updateDocumentTransactionFn } from "../firestore/transactions"
import { DocumentData, FirestoreDocument } from "../types/firestoreDocument"
import { isAppRecentlyLaunched } from "@/utils/appInitTime"
import { logger } from "@/utils/logger"

type QueryTransform<T extends FirestoreDocument> = (
   query: Table<T>,
) => Array<T> | Table<T> | Collection<T>

interface UseCollectionParams<T extends FirestoreDocument> {
   collectionName: string
   transform?: QueryTransform<T>
   onRefetch?: () => void
   enabled?: boolean
   suffixKey?: (string | undefined)[]
}

// Hook pour récupérer un document
export function useDocument<T extends FirestoreDocument>(
   collectionName: string,
   documentId?: string,
) {
   const query = useQuery({
      queryKey: ["document", collectionName, documentId],
      queryFn: async () => {
         if (isAppRecentlyLaunched())
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay))

         // Wait for data to arrive in Dexie with timeout
         let attempts = 0

         while (attempts < dexieQueryMaxAttempts) {
            const data = await dexie.table(collectionName).get(documentId!)
            if (data) {
               logger("useDocument " + collectionName + "/" + documentId, data)
               return data as T
            }
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay))
            attempts++
         }

         console.warn("useDocument " + collectionName + "/" + documentId + " not found")
         throw new Error("Document introuvable après plusieurs tentatives")
      },
      // 5 minutes TODO rm ? and use global value
      gcTime: 1000 * 60 * 5,
      enabled: !!documentId,
   })

   return query
}

// Hook pour récupérer une collection
export function useCollection<T extends FirestoreDocument>({
   collectionName,
   transform = (q) => q,
   onRefetch,
   enabled = true,
   suffixKey,
}: UseCollectionParams<T>) {
   return useQuery<T[]>({
      queryKey: ["collection", collectionName, ...(suffixKey || []), transform.toString()],
      queryFn: async () => {
         if (isAppRecentlyLaunched())
            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay))

         let attempts = 0
         let docs: T[] = []

         while (attempts < dexieQueryMaxAttempts) {
            let results = await transform(dexie.table(collectionName))

            results = results.filter(
               (doc: any) => doc.is_deleted === undefined || doc.is_deleted === false,
            )

            if (results instanceof Array) {
               docs = results
            } else {
               docs = await results.toArray()
            }

            if (docs.length > 0) break

            await new Promise((resolve) => setTimeout(resolve, dexieQueryRetryDelay))
            attempts++
         }

         setTimeout(() => onRefetch?.(), 1)

         return docs
      },
      enabled,
   })
}

// Hook pour créer un nouveau document
export function useCreateDocument<T extends FirestoreDocument>(collectionName: string) {
   return useMutation({
      mutationFn: async (data: DocumentData<T>) => {
         const collectionRef = collection(db, collectionName)

         return runTransaction(
            db,
            (transaction) => createDocumentTransactionFn(transaction, collectionRef, data),
            { maxAttempts: firestoreTransactionMaxAttempts },
         )
      },
   })
}

async function updateDocumentMutationFn<T extends FirestoreDocument>(
   collectionName: string,
   id: string,
   data: Partial<DocumentData<T>>,
) {
   const docRef = doc(db, collectionName, id)

   // TODO return docSnap everywhere
   return await runTransaction(
      db,
      (transaction) => updateDocumentTransactionFn(transaction, docRef, data),
      {
         maxAttempts: firestoreTransactionMaxAttempts,
      },
   )
}

// Hook pour mettre à jour un document
export function useUpdateDocument<T extends FirestoreDocument>(collectionName: string) {
   return useMutation({
      mutationFn: async ({ id, data }: { id: string; data: Partial<DocumentData<T>> }) =>
         updateDocumentMutationFn<T>(collectionName, id, data),
   })
}

// Hook pour supprimer un document
export function useDeleteDocument(collectionName: string) {
   return useMutation({
      mutationFn: async ({ id }: { id: string }) => {
         const data = { is_deleted: true }
         return updateDocumentMutationFn(collectionName, id, data)
      },
   })
}
